'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["previewViewportWidth", "previewViewportHeight", "isSidebarExpanded", "updatePreviewViewportDimensions", "previewWrapperScrollLeft", "updatePreviewWrapperScroll"];
import { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { debounce } from 'underscore';
import { EDIT_PANE_CLASSNAME } from 'ContentEditorUI/constants/dom';
import { getPreviewIframeWrapperElement } from 'ContentEditorUI/lib/layoutDom';
import { getIsSidebarExpanded } from 'ContentEditorUI/redux/selectors/sidebarSelectors';
import { getPreviewViewportWidth, getPreviewViewportHeight, getPreviewWrapperScrollLeft } from 'ContentEditorUI/redux/selectors/inpageSelectors';
import { updatePreviewViewportDimensions as updatePreviewViewportDimensionsAction, updatePreviewWrapperScroll as updatePreviewWrapperScrollAction } from 'ContentEditorUI/redux/actions/inpageActions';
import { jsx as _jsx } from "react/jsx-runtime";
const mapStateToProps = state => ({
  isSidebarExpanded: getIsSidebarExpanded(state),
  previewViewportWidth: getPreviewViewportWidth(state),
  previewViewportHeight: getPreviewViewportHeight(state),
  previewWrapperScrollLeft: getPreviewWrapperScrollLeft(state)
});
const mapDispatchToProps = {
  updatePreviewViewportDimensions: updatePreviewViewportDimensionsAction,
  updatePreviewWrapperScroll: updatePreviewWrapperScrollAction
};

// This is a higher-order-component so it can be the _parent_ of ContentViewContainer, because it needs to know
// when the edit pane has been fully mounted (and that is easier by making it a parent instead of passing
// `hasMounted={true}` to a child component)
const PreviewViewportSizeHOC = WrappedComponent => {
  var _class;
  const PreviewViewportSizeContainer = (_class = class PreviewViewportSizeContainer extends Component {
    constructor(props) {
      super(props);
      this.editPaneRef = /*#__PURE__*/createRef();
      this.debouncedUpdatePreviewViewport = debounce(this.updatePreviewViewport.bind(this), 300);
      this.debouncedUpdatePreviewWrapperScroll = debounce(this.updatePreviewScroll.bind(this), 100);
    }
    componentDidMount() {
      var _this$editPaneRef$cur;
      this.editPane = (_this$editPaneRef$cur = this.editPaneRef.current) !== null && _this$editPaneRef$cur !== void 0 ? _this$editPaneRef$cur : document.getElementsByClassName(EDIT_PANE_CLASSNAME)[0];
      window.addEventListener('resize', this.debouncedUpdatePreviewViewport);
      this.debouncedUpdatePreviewViewport();
      this.previewIframeWrapperElement = getPreviewIframeWrapperElement();
      if (this.previewIframeWrapperElement) {
        this.previewIframeWrapperElement.addEventListener('scroll', this.debouncedUpdatePreviewWrapperScroll);
      }
      window.addEventListener('resize', this.debouncedUpdatePreviewWrapperScroll);
    }
    componentDidUpdate(prevProps) {
      const {
        isSidebarExpanded
      } = this.props;
      const sidebarChanged = prevProps.isSidebarExpanded !== isSidebarExpanded;
      if (sidebarChanged) {
        this.debouncedUpdatePreviewViewport();
      }
    }
    componentWillUnmount() {
      window.removeEventListener('resize', this.debouncedUpdatePreviewViewport);
      window.removeEventListener('resize', this.debouncedUpdatePreviewWrapperScroll);
      if (this.previewIframeWrapperElement) {
        this.previewIframeWrapperElement.removeEventListener('scroll', this.debouncedUpdatePreviewWrapperScroll);
      }
    }
    updatePreviewViewport() {
      const {
        previewViewportWidth,
        previewViewportHeight,
        updatePreviewViewportDimensions
      } = this.props;
      if (this.editPane) {
        const {
          top,
          left,
          width,
          height
        } = this.editPane.getBoundingClientRect();
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const newWidth = windowWidth - left;
        const newHeight = windowHeight - top;
        const isPreviewVisible = width !== 0;
        if (isPreviewVisible && (previewViewportWidth !== newWidth || previewViewportHeight !== newHeight)) {
          updatePreviewViewportDimensions({
            width,
            height
          });
        }
      }
    }
    updatePreviewScroll() {
      const {
        previewWrapperScrollLeft,
        updatePreviewWrapperScroll
      } = this.props;
      if (this.previewIframeWrapperElement && this.editPane) {
        const {
          width: paneWidth
        } = this.editPane.getBoundingClientRect();
        const isPreviewVisible = paneWidth !== 0;
        const scrollLeft = this.previewIframeWrapperElement.scrollLeft;
        if (isPreviewVisible && previewWrapperScrollLeft !== scrollLeft) {
          updatePreviewWrapperScroll({
            scrollLeft
          });
        }
      }
    }
    render() {
      const _this$props = this.props,
        forwardedProps = _objectWithoutPropertiesLoose(_this$props, _excluded);
      return /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, forwardedProps, {
        editPaneRef: this.editPaneRef
      }));
    }
  }, _class.propTypes = {
    previewViewportWidth: PropTypes.number,
    previewViewportHeight: PropTypes.number,
    previewWrapperScrollLeft: PropTypes.number,
    isSidebarExpanded: PropTypes.bool.isRequired,
    updatePreviewViewportDimensions: PropTypes.func.isRequired,
    updatePreviewWrapperScroll: PropTypes.func.isRequired
  }, _class);
  const ConnectedPreviewViewportSizeContainer = connect(mapStateToProps, mapDispatchToProps)(PreviewViewportSizeContainer);
  return ConnectedPreviewViewportSizeContainer;
};
export default PreviewViewportSizeHOC;